/**
 * root-mount-marker.tsx
 *
 * Records the latest mounted private-app wrapper for blank-screen
 * debugging.
 *
 * Primary responsibilities:
 * - Track root wrapper mount order across route changes
 * - Expose the latest stage on `html`, `body`, and `window`
 *
 * Dependencies:
 * - next/navigation
 */

'use client'

import { usePathname } from 'next/navigation'
import { useEffect } from 'react'

interface RootMountEntry {
  stage: string
  pathname: string
  sequence: number
  timestamp: number
  at: string
}

type RootMarkerWindow = Window & {
  __LOSI_LAST_ROOT_MARKER?: RootMountEntry
  __LOSI_ROOT_MARKERS?: RootMountEntry[]
}

const ROOT_MARKER_LIMIT = 100
const ROOT_MARKER_NODE_ID = 'losi-root-marker'

// --------------

function applyRootMarkerDataset(target: HTMLElement | null, entry: RootMountEntry) {
  if (!target) {
    return
  }

  target.dataset.losiRootStage = entry.stage
  target.dataset.losiRootPath = entry.pathname
  target.dataset.losiRootSequence = String(entry.sequence)
  target.dataset.losiRootAt = entry.at
}

// --------------

function upsertRootMarkerNode(entry: RootMountEntry) {
  if (typeof document === 'undefined') {
    return
  }

  let node = document.getElementById(ROOT_MARKER_NODE_ID)

  if (!node) {
    node = document.createElement('div')
    node.id = ROOT_MARKER_NODE_ID
    node.hidden = true
    document.body.appendChild(node)
  }

  node.setAttribute('data-stage', entry.stage)
  node.setAttribute('data-pathname', entry.pathname)
  node.setAttribute('data-sequence', String(entry.sequence))
  node.setAttribute('data-at', entry.at)
  node.textContent = `${entry.sequence}:${entry.stage}:${entry.pathname}`
}

// --------------

export function markRootMount(stage: string, pathname: string | null) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return
  }

  const markerWindow = window as RootMarkerWindow
  const previousEntries = markerWindow.__LOSI_ROOT_MARKERS ?? []
  const previousSequence =
    previousEntries.length > 0 ? previousEntries[previousEntries.length - 1].sequence : 0
  const resolvedPathname = pathname ?? window.location.pathname ?? ''
  const timestamp = Date.now()
  const entry: RootMountEntry = {
    stage,
    pathname: resolvedPathname,
    sequence: previousSequence + 1,
    timestamp,
    at: new Date(timestamp).toISOString()
  }

  markerWindow.__LOSI_LAST_ROOT_MARKER = entry
  markerWindow.__LOSI_ROOT_MARKERS = [...previousEntries, entry].slice(-ROOT_MARKER_LIMIT)

  applyRootMarkerDataset(document.documentElement, entry)
  applyRootMarkerDataset(document.body, entry)
  upsertRootMarkerNode(entry)
}

// --------------

export function useRootMountMarker(stage: string) {
  const pathname = usePathname()

  useEffect(() => {
    markRootMount(stage, pathname)
  }, [pathname, stage])
}

// --------------

interface RootMountMarkerProps {
  stage: string
}

export function RootMountMarker({ stage }: RootMountMarkerProps) {
  useRootMountMarker(stage)

  return null
}
